<template>
	<div v-show="displayValidationRunning">Running Validation Check...</div>
	<div v-show="!displayValidationRunning">
		<!-- Page Label with Selected Option Name -->
		<PageLabel :helpPageName="'Assessments' + props.tabName.replace(/\s+/g, '') + props.subTabName.replace(/\s+/g, '')">
			{{ "Compare" }}
		</PageLabel>

		<div v-if="warningMessage.length > 0">
			<warningBlock :warningMessages="warningMessage" />
		</div>
		<div v-if="optionValidationResults.length > 0">
			<warningBlock header="Warning" :warningMessages="flattenedValidationMessages" />
		</div>

		<!-- Two column layout -->
		<div class="flex flex-row gap-4 mb-4">
			<!-- Left column: Tabs -->
			<div class="flex-[3] min-w-0">
				<div v-show="resultsAvailable" class="i2Panel">
					<div v-show="displayResult">
						<Tabs value="0">
							<TabList @click="changeTab">
								<Tab value="0">
									<FontAwesomeIcon :icon="['far', 'arrows-spin']" title="Results by Life Cycle Stage" />
									<span class="ml-2">
										{{ props.tabName === tabsDefinitions.packaging.value ? "Results by Life Cycle Stage" : "Results by Life Cycle (Summary)" }}
									</span>
								</Tab>
								<Tab value="1"
									><FontAwesomeIcon
										:icon="props.tabName === tabsDefinitions.packaging.value ? ['far', 'boxes-packing'] : ['far', 'arrows-spin']"
										title="Results by Packaging Type"
									/>
									<span class="ml-2">
										{{ props.tabName === tabsDefinitions.packaging.value ? "Results by Packaging Type" : "Results by Life Cycle (Detailed)" }}
									</span></Tab
								>
								<Tab value="2"
									><FontAwesomeIcon :icon="['far', 'flask-round-potion']" title="Results by Material Composition" /><span class="ml-2"
										>Results by Material Composition</span
									></Tab
								>
								<Tab value="3"
									><FontAwesomeIcon :icon="['far', 'trash-can-list']" title="Results by Consumer waste - End of Life" /><span class="ml-2"
										>Results by Consumer waste - End of Life</span
									></Tab
								>
							</TabList>
							<TabPanels>
								<TabPanel v-for="(tab, tabIndex) in tabConfigs" :key="tabIndex" :value="tabIndex.toString()">
									<h2>{{ tab.title }}</h2><br>
									<div class="grid gap-4" :style="{ gridTemplateColumns: `repeat(${compareResults.length}, 1fr)` }">
										<div v-for="(item, index) in compareResults" :key="index" class="flex flex-col bg-white rounded-lg shadow-sm border p-4">
											<component
												:is="tab.component"
												:results="item.result?.[tab.dataKey]"
												:labelText="item.label"
												:activeEmissionFactor="reportsStore.selectedEmissionFactor ?? undefined"
												:showPercentOfTotal="showPercentOfTotal"
												:labelColumnField="tab.labelColumnField"
												:labelColumnHeaderName="tab.labelColumnHeaderName"
											/>
										</div>
									</div>

									<br />

									<!-- Pie charts -->
									<div v-if="showPieChart" class="grid gap-4" :style="{ gridTemplateColumns: `repeat(${compareResults.length}, 1fr)` }">
										<div v-for="(item, index) in compareResults" :key="index" class="flex flex-col bg-white rounded-lg shadow-sm border p-4">
											<CompareResultsChart 
											:finalResult="item.result" 
											:labelText="item.label" 
											:dataKey="tab.dataKey" 
											:showPieChart="true" 
											:emissionFactorName="reportsStore.selectedEmissionFactorName ?? undefined"
											/>
										</div>
									</div>

									<!-- Stacked comparison bar -->
									<div v-else  class="flex flex-col bg-white rounded-lg shadow-sm border p-4">
										<CompareResultsChart 
										:finalResults="compareResults" 
										:dataKey="tab.dataKey" 
										:showPieChart="false"
										:emissionFactorName="reportsStore.selectedEmissionFactorName ?? undefined"
										/>
									</div>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</div>
				</div>
			</div>

			<!-- Right column: Buttons + SelectableList -->
			<div class="flex-1 min-w-0 flex flex-col gap-4 mt-3">
				<!-- Buttons -->
				<div class="flex flex-row items-end justify-end gap-4">
					<Button
						class="btn p-button"
						@click="loadResults"
						:disabled="isCalculateDisabled || isMissingRequiredFields"
						v-tooltip="isMissingRequiredFields ? 'Add Compare Options and select Emission Factor' : ''"
						>{{ resultsAvailable ? "Recalculate" : "Calculate" }}</Button
					>
					<Button class="btn p-button" @click="showAddOptionModal = true">Add Compare Option</Button>
				</div>
				<div class="flex justify-center">
					<div class="flex items-center space-x-4">
						<span class="text-sm font-medium" :class="{ 'text-blue-600': !showPercentOfTotal, 'text-gray-500': showPercentOfTotal }"
							>Absolute Value</span
						>
						<label class="relative inline-flex items-center cursor-pointer">
							<input type="checkbox" v-model="showPercentOfTotal" class="sr-only peer" />
							<div
								class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"
							></div>
						</label>
						<span class="text-sm font-medium" :class="{ 'text-blue-600': showPercentOfTotal, 'text-gray-500': !showPercentOfTotal }"
							>Percent of Total</span
						>
					</div>
				</div>
				<div class="flex justify-center">
					<div class="flex items-center space-x-4">
						<span class="text-sm font-medium" :class="{ 'text-blue-600': !showPieChart, 'text-gray-500': showPieChart }">Bar Chart</span>
						<label class="relative inline-flex items-center cursor-pointer">
							<input type="checkbox" v-model="showPieChart" class="sr-only peer" />
							<div
								class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"
							></div>
						</label>
						<span class="text-sm font-medium" :class="{ 'text-blue-600': showPieChart, 'text-gray-500': !showPieChart }">Pie Chart</span>
					</div>
				</div>
				<div class="flex mt-2 mb-2">
					<I2FormField :label="props.tabName + ' Option'">
						<SelectableList v-model="selectedCompareOptionIds" :options="compareOptions" optionLabel="label" optionValue="id" />
					</I2FormField>
				</div>
				<div class="flex mt-2 mb-2">
					<I2FormField :label="'Emmission Factor'">
						<Select
							v-model="reportsStore.selectedEmissionFactorName"
							:options="reportsStore.emissionFactors"
							optionLabel="emissionFactorName"
							optionValue="emissionFactorName"
							placeholder="Select Emission Factor"
							class="w-full"
						></Select>
					</I2FormField>
				</div>
			</div>
		</div>
	</div>
	<Dialog v-model:visible="showAddOptionModal" modal header="Add Product Format Option" :style="{ width: '600px' }" :closable="false">
		<div class="flex flex-col gap-4">
			<div>
				<I2FormField label="Quantity">
					<InputNumber v-model="tempQuantity" :min="1" :class="{ 'p-invalid': quantityInvalid }" />
				</I2FormField>
				<small v-if="quantityInvalid" class="p-error">Quantity is required</small>
			</div>
			<div>
				<I2FormField label="Format">
					<Select
						v-model="tempFormat"
						:options="resultFormatOptionsWithLabels"
						optionLabel="displayLabel"
						optionValue="id"
						placeholder="Select Format"
						:class="{ 'p-invalid': formatInvalid }"
					></Select>
				</I2FormField>
				<small v-if="formatInvalid" class="p-error">Format is required</small>
			</div>
			<div>
				<I2FormField :label="props.tabName + ' Option'">
					<Select
						v-model="tempGeneralInformationId"
						:options="availableGeneralInformationOptions"
						optionLabel="generalInformationName"
						optionValue="id"
						placeholder="Select Option"
						:class="{ 'p-invalid': generalOptionInvalid }"
					></Select>
					<small v-if="generalOptionInvalid" class="p-invalid">Option is required</small>
				</I2FormField>
			</div>
		</div>

		<template #footer>
			<Button label="Cancel" severity="danger" @click="showAddOptionModal = false" />
			<Button label="Add" severity="primary" @click="addCompareOption" />
		</template>
	</Dialog>
</template>
<script setup lang="ts">
import { ref, onMounted, onActivated, nextTick, watch, computed } from "vue"
import Tabs from "primevue/tabs"
import TabList from "primevue/tablist"
import Tab from "primevue/tab"
import TabPanels from "primevue/tabpanels"
import TabPanel from "primevue/tabpanel"
import Button from "primevue/button"
import InputNumber from "primevue/inputnumber"
import Select from "primevue/select"
import { ApiClient, FileResponse, ExportRequest, FeatureFlagClient, MasterDropdown, ProductFormatClient } from "../../entities/NSwagGenerated"
import { EventBus } from "@/eventBus"
import { library } from "@fortawesome/fontawesome-svg-core"
import {
	faInfoCircle,
	faTrash,
	faPen,
	faFloppyDisk,
	faXmarkLarge,
	faArrowsSpin,
	faBoxesPacking,
	faFlaskRoundPotion,
	faTrashCanList,
	faSpinner,
} from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
library.add(faTrash, faPen, faInfoCircle, faFloppyDisk, faXmarkLarge, faArrowsSpin, faBoxesPacking, faFlaskRoundPotion, faTrashCanList, faSpinner)

import { useErrorHandler } from "../composables/useErrorHandler"

import { useTabsDefinitions } from "../composables/tabsDefinitions"
import { useErrorToaster, useToaster, SystemToastOptions } from "@/composables/toaster"
import SelectableList from "./SelectableList.vue"
import { useRoute } from "vue-router"
import { useReportsStore } from "@/store/reportsStore"
import PageLabel from "./pageLabel.vue"
import warningBlock from "./warningBlock.vue"
import I2FormField from "@/crispify/crispy-components/I2FormField.vue"
import CompareResultsChart from "./compareResultsChart.vue"
import CompareResultsGrid from "./compareResultsGrid.vue"

const showAddOptionModal = ref(false)
const tempGeneralInformationId = ref(null)
const tempQuantity = ref(1)
const tempFormat = ref(null)
const compareOptions = ref<any[]>([])
const selectedCompareOptionIds = ref<string[]>([])

const tabsDefinitions = useTabsDefinitions()

const displayResult = ref(true)
const displayValidationRunning = ref(false)
const displayWarning = ref(false)
const warningMessage = ref<string[]>([])

const apiClient = new ApiClient(import.meta.env.VITE_API_URL?.toString())
const featureFlagClient = new FeatureFlagClient(import.meta.env.VITE_API_URL?.toString())

const route = useRoute()
const props = defineProps({
	assessmentId: {
		type: String,
		required: false,
		default: "",
	},
	tabId: {
		type: String,
		required: false,
		default: "",
	},
	tabName: {
		type: String,
		required: true,
		default: "",
	},
	subTabName: {
		type: String,
		required: false,
		default: "",
	},
	allTabsInformation: {
		type: Object,
		required: true,
		default: "",
	},
})

interface OptionValidationResult {
	optionId: string
	optionName: string
	messages: string[]
}
const optionValidationResults = ref<OptionValidationResult[]>([])

const failingOptionIds = computed(() => new Set(optionValidationResults.value.map((r) => r.optionId)))

const availableGeneralInformationOptions = computed(() =>
	generalInformationOptions.value.filter((o: any) => !failingOptionIds.value.has(o.id))
)

const flattenedValidationMessages = computed(() => [
	`<span style="display:block; font-style:italic; color:#6b7280; font-size:0.875rem; margin-bottom:4px;">Packaging formats with validation errors cannot be used for calculation. Please fix the issues for them if you want to use them for Comparison.</span>`,
	...optionValidationResults.value.flatMap((result) => [
		`<span style="display:block; font-weight:600; font-size:0.9rem; color:#92400e; border-left:2px solid #d97706; padding-left:6px; margin-top:4px;">${result.optionName}</span>`,
		...result.messages.map((msg) => `<span style="display:block; padding-left:16px;">${msg}</span>`),
	]),
])

const compareResults = ref<any[]>([])

const loading = ref(false)
const resultsAvailable = ref(false)
const errorEOL = ref(false)
const reportsStore = useReportsStore()
const isWeighted = ref(false)
const weightedOptionEnabled = ref(false)
const generalInformationId = ref<string>()
const selectedOptionName = ref<string>("")
const calculatedOptionName = ref<string>("")
const isMultiOptionFeatureEnabled = ref(false)
let eventDone = 0

// Calculate Results By label for Packaging tab
const calculateResultsByLabel = ref("Reference Unit")
// Reference unit name (e.g., "Tablet", "ml", "Dose") for use in child component titles
const referenceUnitName = ref("")
// Product format name from General Information
const productFormatName = ref("")

// Packaging result options - quantity and format
const resultQuantity = ref<number>(1)
const resultFormat = ref<MasterDropdown | null>(null)
const resultFormatOptions = ref<MasterDropdown[]>([])
const isCalculateDisabled = ref(false)

// Stores the quantity and format label used in the last calculation (set on Calculate/Recalculate)
const calculatedQuantity = ref<number>(1)
const calculatedFormatLabel = ref<string>("")

// GeneralInformation option selector for Packaging/Distribution
const generalInformationOptions = ref<any[]>([])
const selectedResultGeneralInformationId = ref<string | null>(null)

const showPieChart = ref(false)
const showPercentOfTotal = ref(true)
const submitted = ref(false)

const quantityInvalid = computed(() => submitted.value && !tempQuantity.value)
const formatInvalid = computed(() => submitted.value && !tempFormat.value)
const generalOptionInvalid = computed(() => submitted.value && !tempGeneralInformationId.value)

watch(showAddOptionModal, (value) => {
	if (value) {
		submitted.value = false
	}
})

// Sync sidebar selection when the result dropdown changes
watch(selectedResultGeneralInformationId, async (newId, oldId) => {
	if (!newId || newId === oldId) return
	// Persist selection in DB
	try {
		await apiClient.setGeneralInformationSelected(newId)
	} catch (error) {
		console.error("Error syncing general information selection:", error)
	}
	// Update global tab status
	const tabStatus = props.allTabsInformation?.allTabsStatus?.find((item: any) => item.actualMainTabId === props.tabId)
	if (tabStatus) {
		tabStatus.generalInformationId = newId
	}
})

// Watch quantity to ensure it's always at least 1
watch(resultQuantity, (newValue) => {
	if (newValue === null || newValue === undefined || newValue < 1) {
		resultQuantity.value = 1
	}
})

// Computed to generate format options with dynamic labels and filtering
const resultFormatOptionsWithLabels = computed(() => {
	return resultFormatOptions.value
		.filter((option) => {
			// If Product Format is selected in General Information, hide Reference Unit option
			if (calculateResultsByLabel.value === "Product Format" && option.valueTxt === "Reference Unit") {
				return false
			}
			// If Reference Unit is selected in General Information, hide Product Format option
			if (calculateResultsByLabel.value === "Reference Unit" && option.valueTxt === "Product Format") {
				return false
			}
			return true
		})
		.map((option) => {
			let label = option.valueTxt || ""
			// Add unit name in brackets for Product Format and Reference Unit
			if (option.valueTxt === "Product Format" && productFormatName.value) {
				label = `Product Format [${productFormatName.value}]`
			} else if (option.valueTxt === "Reference Unit" && referenceUnitName.value) {
				label = `Reference Unit [${referenceUnitName.value}]`
			}
			return {
				...option,
				displayLabel: label,
			}
		})
})

// Load result format options from database
const loadResultFormatOptions = async () => {
	if (props.tabName !== tabsDefinitions.packaging.value) {
		return
	}

	try {
		const options = await apiClient.masterDropdownByCategoryKey("resultFormatOption")
		// Sort by ordering field
		options.sort((a, b) => (a.ordering ?? 0) - (b.ordering ?? 0))
		resultFormatOptions.value = options
	} catch (error) {
		console.error("Error loading result format options:", error)
	}
}

// Set the default format selection to first available filtered option
const setDefaultResultFormat = () => {
	const filteredOptions = resultFormatOptionsWithLabels.value
	if (filteredOptions.length === 0) return

	// Check if current selection is still valid in the filtered options
	const currentSelection = resultFormat.value
	const isCurrentSelectionValid = currentSelection && filteredOptions.some((opt) => opt.id === currentSelection.id)

	// If no selection or current selection is not valid, set to first available
	if (!currentSelection || !isCurrentSelectionValid) {
		resultFormat.value = filteredOptions[0] || null
	}
}

// Watch for changes in calculateResultsByLabel to update the selection when it changes
watch(calculateResultsByLabel, () => {
	setDefaultResultFormat()
})

// Load selected option name from general information
const loadSelectedOptionName = async (giId: string) => {
	try {
		// Load all generalInformation options for this assessment/screen and find the one with matching ID
		const options = await apiClient.getCommonAPIGeneralByAssessmentIdAndScreenId(props.assessmentId, props.tabId)
		const gi = options.find((opt) => opt.id === giId)
		if (gi) {
			selectedOptionName.value = gi.generalInformationName || gi.versionName || ""
		}
	} catch (error) {
		console.error("Error loading general information name:", error)
		selectedOptionName.value = ""
	}
}

// Load Calculate Results By type from General Information for Packaging tab
const loadCalculateResultsByType = async () => {
	if (props.tabName !== tabsDefinitions.packaging.value) {
		return
	}

	try {
		const result = await apiClient.getCommonAPIGeneralByAssessmentIdAndScreenId(props.assessmentId, props.tabId)
		if (result && result.length > 0) {
			const packagingGI = result.find((item: any) => !item.notes?.startsWith("consumerEndOfLife_"))
			if (packagingGI) {
				// If packReferenceUnit has a value, it's "Reference Unit"
				// If productFormatId has a value, it's "Product Format"
				if (packagingGI.packReferenceUnit != null && packagingGI.packReferenceUnit.id != null) {
					calculateResultsByLabel.value = "Reference Unit"
					// Store the unit name for child components
					referenceUnitName.value = packagingGI.packReferenceUnit.valueTxt || ""
					productFormatName.value = ""
				} else if (packagingGI.productFormatId != null && packagingGI.productFormatId !== "") {
					calculateResultsByLabel.value = "Product Format"
					referenceUnitName.value = ""
					// Load the product format name
					try {
						const productFormatClient = new ProductFormatClient(import.meta.env.VITE_API_URL?.toString())
						const productFormats = await productFormatClient.list()
						const productFormat = productFormats.find((pf) => pf.id === packagingGI.productFormatId)
						productFormatName.value = productFormat?.formatName || ""
					} catch {
						productFormatName.value = ""
					}
				}
			}
		}
	} catch (error) {
		// Default to Reference Unit if there's an error
		calculateResultsByLabel.value = "Reference Unit"
		referenceUnitName.value = ""
		productFormatName.value = ""
	}
}

// Load generalInformation options for the Option dropdown (Packaging/Distribution)
const loadGeneralInformationOptions = async () => {
	if (props.tabName !== tabsDefinitions.packaging.value && props.tabName !== tabsDefinitions.distribution.value) {
		return
	}
	if (!isMultiOptionFeatureEnabled.value) {
		return
	}

	try {
		const options = await apiClient.getCommonAPIGeneralByAssessmentIdAndScreenId(props.assessmentId, props.tabId)
		let filtered = options.filter((gi: any) => gi.isActive === true)
		// For Packaging, exclude consumerEndOfLife_ internal records
		if (props.tabName === tabsDefinitions.packaging.value) {
			filtered = filtered.filter((gi: any) => !gi.notes || !gi.notes.includes("consumerEndOfLife_"))
		}
		generalInformationOptions.value = filtered

		// Pre-select: use the current generalInformationId, or find wasSelected, or fall back to first
		if (generalInformationId.value && filtered.some((gi: any) => gi.id === generalInformationId.value)) {
			selectedResultGeneralInformationId.value = generalInformationId.value
		} else {
			const selected = filtered.find((gi: any) => gi.wasSelected === true) || filtered[0]
			selectedResultGeneralInformationId.value = selected?.id ?? null
		}
	} catch (error) {
		console.error("Error loading general information options:", error)
		generalInformationOptions.value = []
	}
}

// Check EOL destination setting for packaging tab
const checkEolDestinationSetting = async () => {
	if (props.tabName !== tabsDefinitions.packaging.value) {
		return
	}

	try {
		// Get the current packaging generalInformationId if available
		const packagingGiId =
			generalInformationId.value ||
			props.allTabsInformation?.allTabsStatus?.find((item: any) => item.actualMainTabId === props.tabId)?.generalInformationId

		// Pass packagingGeneralInformationId to check the correct Consumer End Of Life record
		const result = await apiClient.checkIfDestinationMarketSelected(props.assessmentId, packagingGiId || undefined)

		// If successful, remove any previous EOL warning and enable Calculate button
		warningMessage.value = warningMessage.value.filter((msg) => !msg.includes("Destination") && !msg.includes("Consumer End Of Life"))
		isCalculateDisabled.value = false
		displayWarning.value = warningMessage.value.length > 0
	} catch (err: any) {
		console.log("checkEolDestinationSetting: API returned error", err)
		console.log("checkEolDestinationSetting: err.result", err?.result)
		// If error, add warning message and disable Calculate button
		// NSwag stores the response body in err.result for 400 errors
		const errorMessage =
			err?.result?.message ||
			err?.response?.message ||
			"Please open 'Consumer End Of Life' tab and select the Destination Setting field, then click Save"
		// Check if this warning is not already present
		if (!warningMessage.value.some((msg) => msg.includes("Destination") || msg.includes("Consumer End Of Life"))) {
			warningMessage.value.push(errorMessage)
		}
		displayResult.value = false
		displayWarning.value = true
		isCalculateDisabled.value = true
	}
}

onMounted(async () => {
	// Check if weighted calculation option is enabled
	const weightedFlag = await featureFlagClient.getByName("Weighted Calculation Option")
	weightedOptionEnabled.value = weightedFlag?.isEnabled || false

	// Check if multi-option feature flag is enabled
	try {
		const multiOptionFlag = await featureFlagClient.getByName("Allow Multiple Packaging Options per Assessment")
		isMultiOptionFeatureEnabled.value = multiOptionFlag?.isEnabled || false
	} catch (error) {
		isMultiOptionFeatureEnabled.value = false
	}
	// Load result format options for Packaging tab
	await loadResultFormatOptions()

	// Load Calculate Results By type for Packaging tab
	await loadCalculateResultsByType()

	// Set default format selection after both options and type are loaded
	setDefaultResultFormat()

	// Load generalInformation options for Option dropdown
	await loadGeneralInformationOptions()

	// Check EOL destination setting for packaging tab
	await checkEolDestinationSetting()

	// Event listener for result validation triggered from parent
	EventBus.on("validateResult_" + props.tabId, async (data: any) => {
		optionValidationResults.value = []

		if (data != null && data.generalInformationId != null) {
			// Update tracked generalInformationId and load selected option name if changed
			const giChanged = generalInformationId.value !== data.generalInformationId
			if (giChanged || !selectedOptionName.value) {
				await loadSelectedOptionName(data.generalInformationId)
			}
			generalInformationId.value = data.generalInformationId
		}

		displayValidationRunning.value = true
		// Re-load Calculate Results By type when navigating to this tab
		await loadCalculateResultsByType()
		// Update default format selection
		setDefaultResultFormat()
		// Reload generalInformation options for Option dropdown
		await loadGeneralInformationOptions()
		// Re-check EOL destination setting
		await checkEolDestinationSetting()
		// Validate all packaging options in parallel
		await validateAllPackagingOptions()
	})

	// Event listener to force reset results (e.g., when GeneralInformation option changes)
	EventBus.on("forceResetResults_" + props.tabId, () => {
		resultsAvailable.value = false
		displayResult.value = false

		// Clear ALL comparison results
		compareResults.value = []
		optionValidationResults.value = []
	})
})

// Re-check EOL destination setting when component is reactivated (keep-alive)
onActivated(async () => {
	// Re-load Calculate Results By type when component is reactivated
	await loadCalculateResultsByType()
	// Update default format selection
	setDefaultResultFormat()
	// Reload generalInformation options for Option dropdown
	await loadGeneralInformationOptions()
	await checkEolDestinationSetting()
})

const status = ref()

const validateAllPackagingOptions = async () => {
	if (!generalInformationOptions.value.length) return
	displayValidationRunning.value = true
	optionValidationResults.value = []

	const results: OptionValidationResult[] = await Promise.all(
		generalInformationOptions.value.map(async (option: any): Promise<OptionValidationResult> => {
			const name = option.generalInformationName || option.versionName || "Unknown Option"
			try {
				const res = await apiClient.validateResultMatrixByGeneralInformationId(option.id)
				const messages = res?.map((item) => item.messageText!).filter(Boolean) ?? []
				return { optionId: option.id, optionName: name, messages }
			} catch (err: any) {
				return { optionId: option.id, optionName: name, messages: [err?.response?.message || "Validation failed"] }
			}
		})
	)

	optionValidationResults.value = results.filter((r) => r.messages.length > 0)
	displayValidationRunning.value = false
}

// Populate process waste tables before calculating results
async function populateProcessWaste(): Promise<void> {
	const actualTab = props.allTabsInformation?.allTabsStatus?.find((item: any) => item.actualMainTabId === props.tabId)

	if (!actualTab) return

	const genInfoId = actualTab.generalInformationId
	const stepId = actualTab.selectedStepId

	// Populate processWaste for the current step
	if (genInfoId && stepId) {
		try {
			await apiClient.populateProcessWasteAndTable(
				props.assessmentId,
				genInfoId,
				props.tabId,
				stepId,
				"actual", // Load actual saved data
				undefined // region - only needed for EOL
			)
		} catch (err) {
			console.warn("Failed to populate process waste, continuing with calculation:", err)
			// Do not block calculation if population fails
		}
	}
}

const loadResults = async () => {
	// Calculate button simply loads results without validation
	// Validation already happened on page load via EventBus
	resultsAvailable.value = false
	loading.value = true
	displayResult.value = true

	const actualTab = props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId === props.tabId)
	if (props.subTabName.replace(/\s+/g, "") === actualTab?.actualSubTabName && route.query.assessmentId === props.assessmentId) {
		eventDone++
		// For packaging tab, pass quantity and format type parameters
		const quantity = props.tabName === tabsDefinitions.packaging.value ? resultQuantity.value : undefined
		const formatType = props.tabName === tabsDefinitions.packaging.value ? resultFormat.value?.valueTxt : undefined

		try {
			// Populate process waste tables before calculating results
			await populateProcessWaste()

			// Then calculate results - pass selectedResultGeneralInformationId if available
			const giIdParam = selectedResultGeneralInformationId.value || undefined

			compareResults.value = []

			const emissionFactors = reportsStore.selectedEmissionFactorName ? [reportsStore.selectedEmissionFactorName] : undefined
			const selectedCompareOptions = computed(() => compareOptions.value.filter((o) => selectedCompareOptionIds.value.includes(o.id)))

			for (const option of selectedCompareOptions.value) {
				const result = await apiClient.getPackResults(
					props.assessmentId,
					props.tabName,
					emissionFactors,
					isWeighted.value,
					option.quantity,
					option.formatType,
					option.generalInformationId
				)
				compareResults.value.push({
					label: option.label,
					result,
					quantity: option.quantity,
					formatLabel: option.formatLabel,
				})
			}

			resultsAvailable.value = true
			loading.value = false
			errorEOL.value = false

			// Capture the quantity and format used for this calculation
			calculatedQuantity.value = quantity ?? 1
			const matchedOption = resultFormatOptionsWithLabels.value.find((opt) => opt.id === resultFormat.value?.id)
			calculatedFormatLabel.value = matchedOption?.displayLabel || resultFormat.value?.valueTxt || calculateResultsByLabel.value || ""

			// Store the name of the generalInformation used for this calculation
			if (isMultiOptionFeatureEnabled.value && giIdParam) {
				const option = generalInformationOptions.value.find((gi: any) => gi.id === giIdParam)
				calculatedOptionName.value = option?.generalInformationName || ""
			} else {
				calculatedOptionName.value = ""
			}
		} catch (err: any) {
			useErrorHandler().setErrorHandled(err.response)
			let msg: SystemToastOptions = {
				severity: "warn",
				summary: "Warning",
				detail: err.response?.message || err.message,
				life: 5000,
			}

			useToaster(msg)
			errorEOL.value = err.response?.message?.toLowerCase().includes("destination market")
			loading.value = false
			resultsAvailable.value = false
		}
	}
}

watch(
	isWeighted,
	() => {
		if (displayResult.value) {
			loadResults()
		}
	},
	{ flush: "post" }
)

async function changeTab() {
	await nextTick()
	// Chart data is now loaded when user manually selects impact category
}

function addCompareOption() {
	submitted.value = true

	if (quantityInvalid.value || formatInvalid.value || generalOptionInvalid.value) {
		return
	}

	const selectedGeneralOption = generalInformationOptions.value.find((o) => o.id === tempGeneralInformationId.value)
	const selectedFormat = resultFormatOptionsWithLabels.value.find((f) => f.id === tempFormat.value)
	const label = [tempQuantity.value, selectedFormat?.displayLabel + ",", selectedGeneralOption?.generalInformationName].filter(Boolean).join(" ")
	const newId = Date.now().toString()

	compareOptions.value.push({
		id: newId,
		generalInformationId: tempGeneralInformationId.value,
		quantity: tempQuantity.value,
		formatId: tempFormat.value,
		formatType: selectedFormat?.displayLabel,
		label: label,
	})

	selectedCompareOptionIds.value.push(newId)

	// reset fields
	tempGeneralInformationId.value = null
	tempQuantity.value = 1
	tempFormat.value = null

	showAddOptionModal.value = false
}

const tabConfigs = [
	{
		title: "Life Cycle Comparison",
		labelColumnField: "stageName",
		labelColumnHeaderName: "Life Cycle Stage",
		dataKey: "resultByLifeCycleStage",
		component: CompareResultsGrid,
	},
	{
		title: "Packaging Type Comparison",
		labelColumnField: "stepName",
		labelColumnHeaderName: "Packaging Type",
		dataKey: "resultByPackagingType",
		component: CompareResultsGrid,
	},
	{
		title: "Material Composition Comparison",
		labelColumnField: "materialName",
		labelColumnHeaderName: "Material",
		dataKey: "resultByMaterialComposition",
		component: CompareResultsGrid,
	},
	{
		title: "Process Waste Comparison",
		labelColumnField: "materialName",
		labelColumnHeaderName: "Waste Type",
		dataKey: "resultByEol",
		component: CompareResultsGrid,
	},
]

const isMissingRequiredFields = computed(() => {
	return !selectedCompareOptionIds.value.length || !(reportsStore.selectedEmissionFactorNames?.length > 0)
})
</script>
